import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Paper, IconButton, Button, CircularProgress, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import ActionConfirmModal from './ActionConfirmModal';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCurrency } from '../../../utils/currency';

export default function PurchaseOrderModals({
    // Create PO
    createPOModalOpen, setCreatePOModalOpen, isCreatingPO, poForm, setPoForm, suppliers, inventoryItems, 
    handleRemovePOLineItem, handlePOLineItemChange, handleAddPOLineItem, handleCreatePO,
    // Approve PO
    poApproveModalOpen, setPoApproveModalOpen, isReviewingPO, handleApprovePO,
    // Reject PO
    poRejectModalOpen, setPoRejectModalOpen, poRejectReason, setPoRejectReason, handleRejectPO,
    // Receive PO
    poReceiveModalOpen, setPoReceiveModalOpen, isReceivingPO, poReceiveForm, setPoReceiveForm, handleReceivePO
}) {
    const { currencySymbol } = useCurrency();
    return (
        <>
            {/* Create Purchase Order Modal */}
            <Dialog 
                open={createPOModalOpen} 
                onClose={() => !isCreatingPO && setCreatePOModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Create Purchase Order</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle2" color="text.secondary" mb={2}>MASTER ORDER DETAILS</Typography>
                    <Grid container spacing={2} mb={4}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required disabled={isCreatingPO}>
                                <InputLabel id="po-supplier-label" required>Supplier</InputLabel>
                                <Select
                                    labelId="po-supplier-label"
                                    label="Supplier"
                                    value={poForm.supplierId}
                                    onChange={(e) => setPoForm({ ...poForm, supplierId: e.target.value })}
                                >
                                    {suppliers.map(s => (
                                        <MenuItem key={s.id} value={s.id}>{s.companyName || s.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                type="datetime-local"
                                label="Expected Delivery Date"
                                InputLabelProps={{ shrink: true }}
                                value={poForm.expectedDeliveryDate}
                                onChange={(e) => setPoForm({ ...poForm, expectedDeliveryDate: e.target.value })}
                                disabled={isCreatingPO}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Auditing Notes"
                                value={poForm.notes}
                                onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })}
                                disabled={isCreatingPO}
                                placeholder="E.g., Urgent guest linen replenishment"
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" color="text.secondary" mb={2}>LINE ITEMS</Typography>
                    {poForm.items.map((item, index) => (
                        <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, position: 'relative' }}>
                            {poForm.items.length > 1 && (
                                <IconButton 
                                    size="small" 
                                    color="error" 
                                    sx={{ position: 'absolute', top: 4, right: 4 }}
                                    onClick={() => handleRemovePOLineItem(index)}
                                    disabled={isCreatingPO}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required disabled={isCreatingPO} size="small">
                                        <InputLabel id={`po-item-label-${index}`} required>Inventory Item</InputLabel>
                                        <Select
                                            labelId={`po-item-label-${index}`}
                                            label="Inventory Item"
                                            value={item.itemId}
                                            onChange={(e) => handlePOLineItemChange(index, 'itemId', e.target.value)}
                                        >
                                            {inventoryItems.map(invItem => (
                                                <MenuItem key={invItem.id} value={invItem.id}>{invItem.name || invItem.sku || 'Unknown Item'}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        fullWidth
                                        required
                                        size="small"
                                        type="number"
                                        label="Quantity"
                                        placeholder="1"
                                        value={item.quantityOrdered}
                                        onChange={(e) => handlePOLineItemChange(index, 'quantityOrdered', e.target.value)}
                                        disabled={isCreatingPO}
                                        InputProps={{ inputProps: { min: 1 } }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        fullWidth
                                        required
                                        size="small"
                                        type="number"
                                        label={`Unit Cost (${currencySymbol})`}
                                        placeholder="0.00"
                                        value={item.unitCost}
                                        onChange={(e) => handlePOLineItemChange(index, 'unitCost', e.target.value)}
                                        disabled={isCreatingPO}
                                        InputProps={{ inputProps: { min: 0.01, step: "0.01" } }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                    <Button 
                        startIcon={<AddIcon />} 
                        onClick={handleAddPOLineItem}
                        disabled={isCreatingPO}
                    >
                        Add Line Item Row
                    </Button>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setCreatePOModalOpen(false)} 
                        disabled={isCreatingPO}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCreatePO} 
                        variant="contained" 
                        disabled={
                            isCreatingPO || 
                            !poForm.supplierId || 
                            !poForm.expectedDeliveryDate ||
                            poForm.items.length === 0 ||
                            poForm.items.some(item => !item.itemId || !item.quantityOrdered || Number(item.quantityOrdered) <= 0 || item.unitCost === '' || Number(item.unitCost) <= 0)
                        }
                        startIcon={isCreatingPO ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isCreatingPO ? 'Creating...' : 'Create Purchase Order'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Approve PO Modal */}
            <ActionConfirmModal
                open={poApproveModalOpen}
                onClose={() => setPoApproveModalOpen(false)}
                title="Approve Purchase Order"
                message="Are you sure you want to approve this purchase order? This action cannot be undone and will mark the order as authorized for fulfillment."
                onConfirm={handleApprovePO}
                isProcessing={isReviewingPO}
                confirmText="Approve Order"
                confirmColor="primary"
            />

            {/* Reject PO Modal */}
            <Dialog 
                open={poRejectModalOpen} 
                onClose={() => !isReviewingPO && setPoRejectModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reject Purchase Order</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" mb={2}>
                        Please provide a reason for rejecting this purchase order. This will be recorded in the audit trail.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Rejection Reason"
                        value={poRejectReason}
                        onChange={(e) => setPoRejectReason(e.target.value)}
                        disabled={isReviewingPO}
                        required
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setPoRejectModalOpen(false)} 
                        disabled={isReviewingPO}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleRejectPO} 
                        variant="contained" 
                        color="error"
                        disabled={isReviewingPO || !poRejectReason.trim()}
                        startIcon={isReviewingPO ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isReviewingPO ? 'Rejecting...' : 'Reject Order'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Receive PO Modal */}
            <Dialog 
                open={poReceiveModalOpen} 
                onClose={() => !isReceivingPO && setPoReceiveModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Log Shipment Intake</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Supplier Invoice / Reference Number"
                                value={poReceiveForm.supplierInvoiceNumber}
                                onChange={(e) => setPoReceiveForm({ ...poReceiveForm, supplierInvoiceNumber: e.target.value })}
                                disabled={isReceivingPO}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" mb={2}>EXPECTED LINE ITEMS</Typography>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Expected Qty</TableCell>
                                            <TableCell align="right" sx={{ width: '150px' }}>Received Qty</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {poReceiveForm.items.map((item, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{item.itemName}</TableCell>
                                                <TableCell align="right">{item.quantityExpected}</TableCell>
                                                <TableCell align="right">
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={item.quantityReceived}
                                                        onChange={(e) => {
                                                            const newItems = [...poReceiveForm.items];
                                                            newItems[idx].quantityReceived = e.target.value;
                                                            setPoReceiveForm({ ...poReceiveForm, items: newItems });
                                                        }}
                                                        disabled={isReceivingPO}
                                                        InputProps={{ inputProps: { min: 0 } }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setPoReceiveModalOpen(false)} 
                        disabled={isReceivingPO}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleReceivePO} 
                        variant="contained" 
                        color="primary"
                        disabled={
                            isReceivingPO || 
                            !poReceiveForm.supplierInvoiceNumber?.trim() ||
                            poReceiveForm.items.some(item => Number(item.quantityReceived) < 0 || item.quantityReceived === '')
                        }
                        startIcon={isReceivingPO ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isReceivingPO ? 'Recording...' : 'Record Delivery Receipt'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
